import * as SelectPrimitive from '@radix-ui/react-select';
import { cva } from 'class-variance-authority';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../../client/cn';
import { formColors } from '../../../shared/colors';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<SelectOption>;
  className?: string;
  label?: string;
  'aria-label'?: string;
  error?: boolean;
  disabled?: boolean;
}

const selectTriggerVariants = cva(
  'inline-flex cursor-pointer items-center justify-between gap-2 rounded-lg border px-4 py-2 text-left whitespace-nowrap transition-colors duration-200 focus:outline-hidden focus:ring-2 dark:border-strokedark dark:bg-boxdark dark:text-white',
  {
    variants: {
      state: {
        default: `${formColors.select.base} ${formColors.select.focus} hover:border-primary dark:hover:border-primary`,
        error: formColors.select.error,
        disabled: `${formColors.select.disabled} cursor-not-allowed`,
      },
    },
    defaultVariants: {
      state: 'default',
    },
  },
);

export function Select({
  value,
  onChange,
  options,
  className = '',
  label,
  'aria-label': ariaLabel,
  error = false,
  disabled = false,
}: SelectProps) {
  const state = disabled ? 'disabled' : error ? 'error' : 'default';

  return (
    <div>
      {label && (
        <label className="mb-2 block text-sm font-medium text-text-primary dark:text-bodydark">
          {label}
        </label>
      )}
      <SelectPrimitive.Root
        value={value}
        onValueChange={onChange}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger
          className={cn(selectTriggerVariants({ state }), className)}
          aria-label={ariaLabel}
          aria-invalid={error || undefined}
        >
          <SelectPrimitive.Value className="truncate" />
          <SelectPrimitive.Icon asChild>
            <ChevronDown size={16} aria-hidden="true" className="shrink-0" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={4}
            className="z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border border-border-primary bg-white shadow-lg dark:border-strokedark dark:bg-boxdark"
          >
            <SelectPrimitive.Viewport className="p-1">
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  className="relative flex cursor-pointer select-none items-center rounded-md px-3 py-2 pr-8 text-sm text-text-primary outline-hidden transition-colors data-[highlighted]:bg-primary/10 data-[highlighted]:text-primary dark:text-white dark:data-[highlighted]:bg-primary/20 dark:data-[highlighted]:text-primary-200"
                >
                  <SelectPrimitive.ItemText>
                    {option.label}
                  </SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="absolute right-2 inline-flex items-center justify-center text-primary">
                    <Check size={14} aria-hidden="true" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </div>
  );
}
